import { Signal } from '../signal';
import { Seatmap } from './seatmap';
import { Seat, Section } from './types';

export class Selection {
  readonly signal: Signal = new Signal();
  private _lastInteractedSeat: Seat | null = null;
  private _hoveredSeat: Seat | null = null;
  private _hoveredGeneralAdmissionSection: Section | null = null;
  private _section: Section | null = null;
  private _seats = new Set<Seat>();
  private seatmap: Seatmap;

  constructor(seatmap: Seatmap) {
    this.seatmap = seatmap;
  }

  get lastInteractedSeat(): Seat | null {
    return this._lastInteractedSeat;
  }

  set lastInteractedSeat(seat: Seat | null) {
    this._lastInteractedSeat = seat;
    this.signal.emit();
  }

  get hoveredSeat(): Seat | null {
    return this._hoveredSeat;
  }

  set hoveredSeat(seat: Seat | null) {
    this._hoveredSeat = seat;
    this.signal.emit();
  }

  get hoveredGeneralAdmissionSection(): Section | null {
    return this._hoveredGeneralAdmissionSection;
  }

  set hoveredGeneralAdmissionSection(section: Section | null) {
    this._hoveredGeneralAdmissionSection = section;
    this.signal.emit();
  }

  get seats(): Set<Seat> {
    return this._seats;
  }

  set seats(seats: Set<Seat>) {
    this._seats = seats;
    this._section = null;
    this.signal.emit();
  }

  get section(): Section | null {
    return this._section;
  }

  set section(section: Section | null) {
    this._section = section;
    this._seats.clear();
    this.signal.emit();
  }

  get type(): 'seats' | 'section' | null {
    if (this._section) return 'section';
    if (this._seats.size > 0) return 'seats';
    return null;
  }

  get lastSelectedSeat(): Seat | null {
    if (!this.lastInteractedSeat || this.type !== 'seats') return null;
    return this.seats.has(this.lastInteractedSeat) ? this.lastInteractedSeat : null;
  }

  addSeatsInRowToSelection(): void {
    if (this.type !== 'seats' || !this.lastInteractedSeat) return;

    const selectedSectionId = this.lastInteractedSeat.section.id;
    const selectedRow = this.lastInteractedSeat.row;
    if (!selectedSectionId || !selectedRow) return;

    const seatsInRow =
      this.seatmap.layout.sections
        .find((section) => section.id === selectedSectionId)
        ?.seats.filter((seat) => seat.row === selectedRow) ?? [];

    this.addSeats(seatsInRow);
  }

  toggleSeatsInbetweenLastInteractionAnd(seat: Seat): void {
    if (this.type !== 'seats' || !this.lastInteractedSeat) return;

    const seatsInBetween = this.seatsBetween(this.lastInteractedSeat, seat);

    if (this.lastSelectedSeat) {
      this.addSeats(seatsInBetween);
    } else {
      this.removeSeats(seatsInBetween);
    }
  }

  addSeatsInSectionToSelection(section: Section): void {
    const seats = section.seats;
    this.addSeats(seats);
  }

  toggleSeatsInSection(section: Section): void {
    const seats = section.seats;
    const containsAllSeats = seats.every((e) => this.seats.has(e));
    containsAllSeats ? this.removeSeats(seats) : this.addSeats(seats);
  }

  selectSection(section: Section): void {
    if (section.generalAdmission) {
      this.section = section;
    } else {
      this.seats = new Set(section.seats);
    }
  }

  addSeats(seats: Seat[]): void {
    if (this.section) {
      this._section = null;
    }
    seats.forEach((e) => this.seats.add(e));
    this.signal.emit();
  }

  removeSeats(seats: Seat[]): void {
    if (this.section) {
      this._section = null;
    }
    seats.forEach((e) => {
      if (this.seats.has(e)) {
        this.seats.delete(e);
      }
    });
    this.signal.emit();
  }

  toggleSeat(seat: Seat): void {
    if (this.seats.has(seat)) {
      this.seats.delete(seat);
    } else {
      this.seats.add(seat);
      this._section = null;
    }
    this._lastInteractedSeat = seat;
    this.signal.emit();
  }

  toggleSection(section: Section): void {
    if (this._section === section) {
      this.section = null;
    } else {
      this.section = section;
    }
  }

  clear(): void {
    this._seats.clear();
    this._lastInteractedSeat = null;
    this.section = null;
  }

  private seatsBetween(a: Seat, b: Seat): Seat[] {
    if (a.section.id !== b.section.id || a.row !== b.row) return [];
    const selectedSectionId = a.section.id;
    const selectedRow = a.row;
    let fromNumber = Number(a.name);
    let toNumber = Number(b.name);

    if (isNaN(fromNumber) || isNaN(toNumber)) return [];
    if (fromNumber > toNumber) {
      const x = fromNumber;
      fromNumber = toNumber;
      toNumber = x;
    }

    const seatsInBetween =
      this.seatmap.layout.sections
        .find((section) => section.id === selectedSectionId)
        ?.seats.filter((seat) => {
          return seat.row === selectedRow && Number(seat.name) >= fromNumber && Number(seat.name) <= toNumber;
        }) ?? [];

    return seatsInBetween;
  }
}
